import React from 'react';
import { Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLoginForm from '../../hooks/useLoginForm';
import SiteLogo from '../../components/SiteLogo';

/**
 * Login Variant 3 — Minimal Card
 * Clean white card on soft gradient background, Apple-inspired with rounded accents and bold typography.
 */
const LoginMinimalCard = () => {
  const { t } = useTranslation('auth');
  const {
    email, password, showPassword, rememberMe, loading, error,
    validationErrors, showCookieNotice,
    setShowPassword, setRememberMe,
    handleEmailChange, handlePasswordChange, handleSubmit,
    handleAcceptAll, handleOnlyNecessary,
    socialProviders, handleSocialLogin,
  } = useLoginForm();

  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const registerUrl = returnTo ? `/register?returnTo=${encodeURIComponent(returnTo)}` : '/register';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/40 to-indigo-50/30 flex flex-col overflow-y-auto">
      {/* Top Nav */}
      <nav className="flex items-center justify-between px-8 py-6 max-w-7xl mx-auto w-full">
        <Link to="/" className="flex items-center gap-2">
          <SiteLogo forceLight className="h-7" />
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 hidden sm:inline">{t('login.noAccount')}</span>
          <Link to={registerUrl} className="px-5 py-2.5 bg-gray-900 text-white rounded-full text-sm font-bold hover:bg-gray-800 transition-colors">
            {t('login.signUp')}
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-[440px]">
          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/60 border border-gray-100 p-10">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-extrabold text-gray-900 mb-2">{t('login.title')}</h1>
              <p className="text-gray-500 text-[15px]">{t('login.welcomeBackAlt')}</p>
            </div>

            {/* Social Buttons — Top */}
            {socialProviders.length > 0 && (
              <div className="space-y-3 mb-8">
                {socialProviders.includes('google') && (
                  <button type="button" onClick={() => handleSocialLogin('google')} className="w-full flex items-center justify-center gap-3 py-3.5 bg-white border-2 border-gray-200 rounded-2xl hover:bg-gray-50 hover:border-gray-300 transition-all text-sm font-semibold text-gray-700">
                    <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    {t('login.continueWithGoogle')}
                  </button>
                )}
                {socialProviders.includes('twitter') && (
                  <button type="button" onClick={() => handleSocialLogin('twitter')} className="w-full flex items-center justify-center gap-3 py-3.5 bg-gray-900 border-2 border-gray-900 rounded-2xl hover:bg-gray-800 transition-all text-sm font-semibold text-white">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    {t('login.continueWithX')}
                  </button>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="relative mb-8">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center"><span className="bg-white px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('login.orUseEmail')}</span></div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('login.email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder={t('login.emailPlaceholder')}
                  className={`w-full px-4 py-3.5 border-2 rounded-2xl text-[15px] focus:outline-none transition-all ${
                    validationErrors.email
                      ? 'border-red-300 focus:border-red-400 bg-red-50/30'
                      : 'border-gray-200 focus:border-gray-900 bg-gray-50/50 focus:bg-white'
                  }`}
                  required
                />
                {validationErrors.email && <p className="text-red-500 text-xs mt-1.5 ms-1">{validationErrors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-gray-700">{t('login.password')}</label>
                  <Link to="/forgot-password" className="text-xs text-sky-500 hover:text-sky-600 font-semibold">{t('login.forgot')}</Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    placeholder={t('login.passwordPlaceholder')}
                    className={`w-full px-4 py-3.5 pe-12 border-2 rounded-2xl text-[15px] focus:outline-none transition-all ${
                      validationErrors.password
                        ? 'border-red-300 focus:border-red-400 bg-red-50/30'
                        : 'border-gray-200 focus:border-gray-900 bg-gray-50/50 focus:bg-white'
                    }`}
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {validationErrors.password && <p className="text-red-500 text-xs mt-1.5 ms-1">{validationErrors.password}</p>}
              </div>

              {/* Error */}
              {error && (
                <div className="p-3.5 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm font-medium">{error}</div>
              )}

              {/* Remember */}
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="w-[18px] h-[18px] rounded-md border-2 border-gray-300 text-gray-900 focus:ring-gray-900 focus:ring-offset-0" />
                <span className="text-sm text-gray-600">{t('login.keepSignedIn')}</span>
              </label>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="group w-full py-4 bg-gray-900 text-white rounded-2xl font-bold text-[15px] hover:bg-gray-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t('login.signingIn')}</>
                ) : (
                  <>
                    {t('login.title')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform rtl:-scale-x-100" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Below card */}
          <p className="text-center text-sm text-gray-500 mt-6">
            {t('login.noAccount')}{' '}
            <Link to={registerUrl} className="text-gray-900 font-bold hover:underline">{t('login.signUpForFree')}</Link>
          </p>

          <p className="text-center text-[11px] text-gray-400 mt-4 px-4">
            {t('login.termsAgreement')}{' '}
            <a href="/page/terms-of-service" className="underline hover:text-gray-600">{t('login.termsOfService')}</a> {t('common:label.and')}{' '}
            <a href="/page/privacy-policy" className="underline hover:text-gray-600">{t('login.privacyPolicy')}</a>, {t('login.ageConfirm')}
          </p>
        </div>
      </div>

      {/* Cookie Notice */}
      {showCookieNotice && (
        <div className="fixed bottom-0 start-0 end-0 z-50">
          <div className="bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-gray-600">
                {t('login.cookieMessage')}{' '}
                <a href="/page/cookie-policy" className="text-sky-500 font-medium hover:underline">{t('login.cookieLearnMore')}</a>
              </p>
              <div className="flex gap-3">
                <button onClick={handleOnlyNecessary} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">{t('login.necessaryOnly')}</button>
                <button onClick={handleAcceptAll} className="px-5 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-colors">{t('login.acceptAll')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginMinimalCard;
